// Webflow Lightbox widget (.w-lightbox): a link that opens its media (here, always a video)
// in a full-screen overlay. Reuses Webflow's .w-lightbox-* styles from webflow.css.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { WfProps } from './element';

interface LightboxItem {
  url: string;
  type: string;
  width?: number;
  height?: number;
  html?: string;
  [field: string]: unknown;
}

/** Turns a YouTube watch URL into a direct embed URL (Webflow proxied these through Embedly). */
function embedUrl(url: string): string | undefined {
  const m = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/.exec(url);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1` : undefined;
}

export function Lightbox({
  config,
  as: Tag = 'a',
  children,
  ...props
}: WfProps & { config: { items: LightboxItem[]; group?: string } }) {
  const [open, setOpen] = useState(false);
  const item = config.items[0];

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    document.documentElement.classList.add('w-lightbox-noscroll');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.classList.remove('w-lightbox-noscroll');
    };
  }, [open]);

  const src = item ? embedUrl(item.url) : undefined;
  const width = item?.width ?? 940;
  const height = item?.height ?? 528;

  return (
    <>
      <Tag
        {...props}
        aria-label="open lightbox"
        aria-haspopup="dialog"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          if (item) setOpen(true);
        }}
      >
        {children}
      </Tag>
      {open &&
        src &&
        createPortal(
          <div
            className="w-lightbox-backdrop"
            role="dialog"
            aria-modal="true"
            tabIndex={0}
            style={{ transition: 'opacity 300ms', opacity: 1 }}
          >
            <div className="w-lightbox-container">
              <div className="w-lightbox-content">
                <div className="w-lightbox-view" tabIndex={0} style={{ opacity: 1 }} onClick={() => setOpen(false)}>
                  <div className="w-lightbox-frame">
                    <figure className="w-lightbox-figure">
                      <img
                        className="w-lightbox-img w-lightbox-image"
                        alt=""
                        src={`data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%22${width}%22%20height=%22${height}%22/%3E`}
                      />
                      <iframe
                        className="w-lightbox-embed"
                        src={src}
                        width={width}
                        height={height}
                        title="YouTube embed"
                        allow="autoplay; fullscreen; encrypted-media; picture-in-picture;"
                        allowFullScreen
                      />
                    </figure>
                  </div>
                </div>
                <div
                  className="w-lightbox-control w-lightbox-close"
                  role="button"
                  aria-label="close lightbox"
                  tabIndex={0}
                  onClick={() => setOpen(false)}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
